import { registerLocaleData, formatDate } from '@angular/common';
import localeUk from '@angular/common/locales/uk';
registerLocaleData(localeUk, 'uk');

import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { map, concatMap, switchMap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Title } from "@angular/platform-browser";
import { MessageService, FilterService } from 'primeng/api';
import { Table } from 'primeng/table';

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { Currency, CurrencyRate, CurrencyRateListItem } from 'src/app/shared/currency';
import { RestApiService, crListFetchParams } from 'src/app/services/rest-api.service';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-currency-rates',
  templateUrl: './currency-rates.component.html',
  styleUrls: ['./currency-rates.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Currency rates list and inline editor.
 *
 * `listOptions$` is a Subject piped through `debounceTime(300)` + `distinctUntilChanged` +
 * `switchMap` that first fetches the total count (`st:0, ft:0`) to set `totalRecords`, then
 * immediately issues a second request for the current page window — giving a two-phase load
 * without a full reload on every keystroke.
 *
 * Inline row editing uses a shadow clone array (`currencyRatesListClone`) keyed by
 * `cloneIndex`. The max+1 algorithm in `onPpRowEditInit` assigns a unique index to every
 * newly opened edit row so that multiple rows can be edited concurrently.
 *
 * `onNewCurrencyRate` pre-inserts a sentinel row (CRID == -2) at position 0 and immediately
 * calls `initRowEdit` on it; `onPpRowEditSave` POSTs it (the API answers `{ CRID }`), completes the row with the
 * code/name of the chosen currency and reloads the table. PUT fetches the real row back via
 * `getCurrencyRateByRowID`.
 *
 * A custom PrimeNG filter `gtOrZero` is registered in `ngOnInit` to support rate-value
 * range filtering. `_loadHeaderItems` populates the currency dropdown on init.
 */
export class CurrencyRatesComponent implements OnInit, OnDestroy {

  private globalSettinsData: GlobalSettingsData;

  currencyRatesList: CurrencyRateListItem[];
  currencyRatesListClone: CurrencyRateListItem[] = [];
  currencyRatesListDS: CurrencyRateListItem[];
  selectedCurrencyRatesItems: CurrencyRateListItem[];
  selectedCurrencyRatesItem: CurrencyRateListItem;

  /** `true` when a row is selected in the table. */
  public get isThereSelectedRow(): boolean { return this.selectedCurrencyRatesItem ? true : false; }

  private currRateQuery: Subscription;
  private currencyQuery: Subscription;
  private ratesQuery: Subscription;
  private editRatesQuery: Subscription;

  currencyList: Currency[] = [];
  currencyEditorList: Currency[] = [];
  currencyListDS: Currency[] = [];

  loading: boolean = false;

  editMode: boolean = false;

  selectAllCurrencies: boolean = false;

  rows: number = 20;
  totalRecords: number = 20;    // First kick ????

  dateFormat: string = 'dd.mm.yy';

  today: Date;

  @ViewChild("CRT1") public CRT1: Table;

  selectedCurrencies: Currency[] = [];

  currDateFilter: string = '';
  currNameFilter: string = '';
  currRateFilter: string = '';

  private listOptions$ = new Subject<crListFetchParams>();

  constructor(
    private restapi: RestApiService,
    private filterService: FilterService,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title
  ) { }

  /**
   * Copy of the table's filter object for the API request, with the `CURCODE` currency items cut down to `{ CURID }`
   * (all the API reads). Never mutates `fs` -- see the call site.
   */
  static toRequestFilters(fs: any): any {
    if (!fs || !fs['CURCODE']) { return fs; }
    const trim = (f: any) => (f && Array.isArray(f.value)) ? { ...f, value: f.value.map((e: Currency) => ({ CURID: e.CURID })) } : f;
    const f = fs['CURCODE'];
    return { ...fs, CURCODE: Array.isArray(f) ? f.map(trim) : trim(f) };
  }

  /**
   * Maps currency items restored from a saved table state (possibly `{ CURID }`-only, as an older build saved them)
   * back onto the loaded `currencyList` objects, which is what the multiselect matches its options by. Items without
   * a match are kept as they are; a missing list stays missing.
   */
  private _resolveCurrencies(items: Currency[] | null | undefined): Currency[] | null | undefined {
    if (!Array.isArray(items)) { return items; }
    return items.map(e => (this.currencyList || []).find(c => c.CURID == e?.CURID) ?? e);
  }

  /** Loads the currency list for the filter and editor dropdown. */
  private _loadHeaderItems(): void {

    this.currencyQuery
      = this.restapi.getCurrency()
        .pipe(map(data => { this.currencyList = data; this.currencyEditorList = [...data]; }))
      .subscribe(() => {
        if (this.currencyListDS) {
        /*
         * Reload filtered state info
         */
          this.currencyListDS = this._resolveCurrencies(this.currencyListDS);
        }
          console.log('*** CurrencyRatesComponent _loadHeaderItems: ');
        });
  }

  /** PrimeNG `onLazyLoad` handler — pushes the requested page window to `listOptions$`. */
  loadCurrencyRates(event) {
    console.log('*** CurrencyRatesComponent filterEvent: ' + JSON.stringify(event));

  //event.first = First row offset
  //event.rows = Number of rows per page
  //event.sortField = Field name to sort with
  //event.sortOrder = Sort order as number, 1 for asc and -1 for dec
  //filters: FilterMetadata object having field as key and filter value, filter matchMode as value

    let options: crListFetchParams = {
      st: event.first ,
      ft: event.rows,
      fs: event.filters,
      sp: {          // Sorting parameters object
        sf: event.sortField,
        so: event.sortOrder
      }
    };

    this.listOptions$.next(options);
  }

  /** Unsubscribes all active queries. */
  ngOnDestroy(): void {
    console.log('*** CurrencyRatesComponent ngOnDestroy: ');

    if (this.currRateQuery) { this.currRateQuery.unsubscribe(); }

    if (this.currencyQuery) { this.currencyQuery.unsubscribe(); }

    if (this.ratesQuery) { this.ratesQuery.unsubscribe(); }

    if (this.editRatesQuery) { this.editRatesQuery.unsubscribe(); }
  }

  /** Registers the custom `gtOrZero` filter, loads currency dropdown, and wires the `listOptions$` two-phase load pipeline. */
  ngOnInit(): void {
    console.log('*** CurrencyRatesComponent ngOnInit: ');

    this.titleService.setTitle('Валюты и курсы');

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.filterService.register('gtOrZero', (value: number, filter: number) => (
        ((filter == 0) && (value == 0)) || ((filter > 0) && (value > filter))
      )
    );

    this.today = new Date();
    this.today = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() + 1);

    this._loadHeaderItems();
    this.loading = true;

    this.currRateQuery =
      this.listOptions$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(options => {

          console.log('*** CurrencyRatesComponent in createSubscription: pgn=(' + JSON.stringify(options) + ')');

          /*
           * Reduce size of parameters' query -- on a COPY: `options.fs` is the table's live filter object, which
           * PrimeNG saves to localStorage and `onStateRestore` feeds back into the multiselect. Trimming it in place
           * left `{ CURID }`-only items there, which match no option, so the filter showed "null".
           */
          options = { ...options, fs: CurrencyRatesComponent.toRequestFilters(options.fs) };
          return this.restapi.getCurrencyRates({ ...options, st: 0, ft: 0 });
        })

      )
        .subscribe((data) => {
          this.totalRecords = data.length;

          let options: crListFetchParams = {
            st: this.CRT1.first,
            ft: this.CRT1.rows,
            fs: CurrencyRatesComponent.toRequestFilters(this.CRT1.filters),
            sp: {          // Sorting parameters object
              sf: this.CRT1.sortField,
              so: this.CRT1.sortOrder
            }
          };

          this.ratesQuery = this.restapi.getCurrencyRates(options).subscribe((data) => {
            console.log('*** CurrencyRatesComponent in createSubscription records: ' + data.length);
            this.currencyRatesList = data;

            this.currencyRatesList.forEach(e => {
              e.cloneIndex = 0;
              e.CRDATE = new Date(e.CRDATE);      // Very strange assignment - for correct work of p-calendar

              //e.DATECOPY = new Date(e.CRDATE);
              //e.CRDATE = new Date(e.DATECOPY);
            });
            this.loading = false;

            this.today = new Date();
            this.today = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() + 1);

          });
        });
}

  /** Stores the selected row reference. */
  onRowSelect(event) {
    console.log('*** CurrencyRatesComponent onRowSelect: ' + JSON.stringify(event.data))
//    this.messageService.add({ severity: 'info', summary: 'CurrencyRateRow Selected', detail: event.data.CRID });
  }

  /** Clears the selected row reference. */
  onRowUnselect(event) {
    console.log('*** CurrencyRatesComponent onRowUnselect: ' + JSON.stringify(event.data))
  }

  /** Pushes current table state to `listOptions$` to trigger a reload. */
  reloadCurrencyRatesTable() {

    let options: crListFetchParams = {
      st: this.CRT1.first,
      ft: this.CRT1.rows,
      fs: this.CRT1.filters,
      sp: {          // Sorting parameters object
        sf: this.CRT1.sortField,
        so: this.CRT1.sortOrder
      }
    };
    this.listOptions$.next(options);

  }

  /** Table filter-change handler — delegates to `reloadCurrencyRatesTable`. */
  filterEvent(event) {
    console.log('*** CurrencyRatesComponent filterEvent: ' + JSON.stringify(event));

    this.reloadCurrencyRatesTable();
  }

  /** Applies the currency multiselect value as a PrimeNG `in` filter. */
  onCurrListChange(event) {
    console.log('*** CurrencyRatesComponent onCurrListChange: ' + JSON.stringify(event));
    this.CRT1.filter(event.value, 'CURCODE', 'in');
  }

  /** Toggles all-currencies selection and re-applies the filter. */
  onAllCurrListChange(event) {
    console.log('*** CurrencyRatesComponent onAllCurrListChange: ' + JSON.stringify(event));
    this.currencyListDS = event.checked ? [...this.currencyList] : [];
    this.selectAllCurrencies = event.checked;
    this.CRT1.filter(this.currencyListDS, 'CURCODE', 'in');
  }

  /** Table column-filter handler — delegates to `reloadCurrencyRatesTable`. */
  filterProdTable(event) {
    console.log('*** CurrencyRatesComponent filterTable: ' + JSON.stringify(event.filters));

    this.reloadCurrencyRatesTable();
  }

  /** Logs the saved table state (diagnostic only). */
  onStateSave(state) {
    console.log('*** CurrencyRatesComponent onStateSave: ' + JSON.stringify(state));
  }

  /** Restores filter field bindings and row selection from the saved table state. */
  onStateRestore(state) {
    console.log('*** CurrencyRatesComponent onStateRestore: ' + JSON.stringify(state));

    if (state.filters) {
      for (let f in state.filters) {
        let fltItem = state.filters[f];
        let fltValue = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;
        switch (f) {

          case 'CURCODE':
            this.currencyListDS = this._resolveCurrencies(fltValue);
            break;

          case 'CURNAME': this.currNameFilter = fltValue; break;

          case 'CRDATE': this.currDateFilter = fltValue; break;

          case 'CRVALUE': this.currRateFilter = fltValue; break;

          case 'global':
            //            this.documentGlobalFilter = fltValue;
            break;

          default:
            console.log(JSON.stringify(f));
        }
      }
    }
    if (state.selection) {
      this.selectedCurrencyRatesItem = state.selection;
    }
  }

  /** Resets all filters and triggers a full reload. */
  clearOrResetTableFilter(event) {
    console.log('*** CurrencyRatesComponent clearOrResetTableFilter: ' + JSON.stringify(event));

    this.currencyListDS = null;
    this.currDateFilter = null;
    this.currNameFilter =
    this.currRateFilter = '';

    this.CRT1.clearState();
    this.CRT1.clear();

    this.reloadCurrencyRatesTable();
  }

  /** Logs the date selected in the inline row editor. */
  onDateSelect(event, item: CurrencyRateListItem) {
    console.log('*** CurrencyRatesComponent onDateSelect: ' + JSON.stringify(event));
    console.log('*** CurrencyRatesComponent onDateSelect: ' + JSON.stringify(item));
  }

  /** Inserts a sentinel row (CRID == -2) at the top of the list and immediately enters edit mode. */
  onNewCurrencyRate(event) {
    console.log('*** CurrencyRatesComponent onNewCurrencyRate: ' + JSON.stringify(event));

    let newCurId = this.selectedCurrencyRatesItem ? this.selectedCurrencyRatesItem.CURID : this.globalsettingsservice.getBalanceCurrencyId();

    /*
     * TODO: Let's play with date
     * 
    environment.production || console.log(ddd);
    environment.production || console.log(rateDate);
    environment.production || console.log(rateDate2);


    environment.production || console.log(ddd.toDateString());
    environment.production || console.log(rateDate.toDateString());
    environment.production || console.log(rateDate2.toDateString());

    environment.production || console.log(ddd.toString());
    environment.production || console.log(rateDate.toString());
    environment.production || console.log(rateDate2.toString());

    environment.production || console.log(ddd.toISOString());
    environment.production || console.log(rateDate.toISOString());
    environment.production || console.log(rateDate2.toISOString());

    environment.production || console.log(ddd.toUTCString());
    environment.production || console.log(rateDate.toUTCString());
    environment.production || console.log(rateDate2.toUTCString());

     */

    let ddd: Date = new Date();
//    const rateDate = new Date(ddd.getFullYear(), ddd.getMonth(), ddd.getDate(), 0, 0, 0);
    var rateDate = new Date();
    rateDate.setUTCFullYear(ddd.getFullYear(), ddd.getMonth(), ddd.getDate());
    rateDate.setUTCHours(0, 0, 0, 0);

    const rateDate2 = new Date(ddd.getUTCFullYear(), ddd.getUTCMonth(), ddd.getUTCDate(), 0, 0, 0);

    this.currencyRatesList.unshift({ CRID: -2, CURID: newCurId, CURCODE: '', CURNAME: '', CRDATE: rateDate2, CRVALUE: 1.01, cloneIndex: 0 });

    environment.production || console.table(this.currencyRatesList[0]);

    let ppe: CurrencyRateListItem = this.currencyRatesList.find(e => e.CRID == -2);
    this.onPpRowEditInit(ppe);
    this.CRT1.initRowEdit(ppe);
  }

  /** Assigns a unique `cloneIndex` and pushes a shadow copy to `currencyRatesListClone` for concurrent-edit tracking. */
  onPpRowEditInit(esg: CurrencyRateListItem) {
    console.log('*** CurrencyRatesComponent in onPpRowEditInit: entry');
    environment.production || console.table(esg);

    let newCloneIndex: number = 0;

    /*
     * Quite tricky algoritm:
     * Because we can edit simultaneously more than one row in the table.
     * Therefore for every edited row we have to create cloned copy in shadow array [securityGroupsClone]
     * We can't predict the order of starting and ending the edit process
     * 
     * So - Search for max cloneIndex in current table and assign +1 to newly created row copy
     *
     * When row either cancelled or saved - clone index resetted to 0, clone copy deleted from shadow array
     *
     */
    this.currencyRatesList.forEach(pp => { newCloneIndex = Math.max(newCloneIndex, pp.cloneIndex) });

    console.log('*** CurrencyRatesComponent in onPpRowEditInit: newCloneIndex is: ' + newCloneIndex);

    esg.cloneIndex = ++newCloneIndex;
    this.currencyRatesListClone.push({ ...esg });

    this.editMode = true;

    console.log('*** CurrencyRatesComponent in onPpRowEditInit: clone created');
    environment.production || console.table(esg);
    console.log('*** CurrencyRatesComponent in onPpRowEditInit: clone ');
    environment.production || console.table(this.currencyRatesListClone);
  }

  /** Restores the original row from the clone array or removes the sentinel row if cancelled before save. */
  onPpRowEditCancel(esg: CurrencyRateListItem, index: number) {
    console.log('*** CurrencyRatesComponent in onPpRowEditCancel: ' + index);
    environment.production || console.table(esg);

    let ppid = this.currencyRatesListClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);

    if (esg.CRID == -2) {
      // Cancel the newly created record
      // simply delete

      this.currencyRatesList.splice(index, 1);

    } else {
      // Cancel editing of existing record
      // have to reload it from database

      this.currencyRatesList[index] = { ...this.currencyRatesListClone[ppid] };
      this.currencyRatesList[index].cloneIndex = 0;
    }

    this.currencyRatesListClone.splice(ppid, 1);

    this.editMode = false;

    console.log('*** CurrencyRatesComponent in onPpRowEditSave: currencyRatesList');
    environment.production || console.table(this.currencyRatesList);
    console.log('*** CurrencyRatesComponent in onPpRowEditSave: currencyRatesListClone ');
    environment.production || console.table(this.currencyRatesListClone);
  }

  /** POSTs new rows or PUTs existing ones, then refreshes the saved row via `getCurrencyRateByRowID`. */
  onPpRowEditSave(esg: CurrencyRateListItem) {
    console.log('*** CurrencyRatesComponent in onRowEditSave: ');
    environment.production || console.table(esg);

    let ppid = this.currencyRatesListClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);
    let ncid = this.currencyRatesList.findIndex(e => e.cloneIndex == esg.cloneIndex);

    /*
     * Below the different update strategy for new and existing records
     */

    if (esg.CRID == -2) {   // Post new row

      this.editRatesQuery = this.restapi.postCurrencyRates(esg)
        .subscribe((created) => {
          console.log('*** CurrencyRatesComponent created record: ' + JSON.stringify(created));
          this.currencyRatesListClone.splice(ppid, 1);
          // The API answers `{ CRID }` only: take the code/name from the currency list the row was picked from,
          // so the row is complete at once (and does not depend on a second request); the reload below
          // replaces it with the stored row.
          const cur = (this.currencyEditorList || []).find(c => c.CURID == esg.CURID);
          if (ncid >= 0) {
            this.currencyRatesList[ncid] = {
              ...esg, CRID: created.CRID, CURCODE: (cur?.CURCODE || '').trim(), CURNAME: (cur?.CURNAME || '').trim(), cloneIndex: 0
            };
          }
          this.editMode = false;
          this.reloadCurrencyRatesTable();
        });

    } else {                // Put existing row

      this.editRatesQuery = this.restapi.putCurrencyRates(esg)
        .pipe(
          concatMap(count => this.restapi.getCurrencyRateByRowID(esg.CRID))).subscribe((row) => {
            console.log('*** CurrencyRatesComponent map read created record: ' + JSON.stringify(row));
            this.currencyRatesListClone.splice(ppid, 1);
            /*
             *  TODO: Optimise prodpart loading after update (put)
             */
            row[0].cloneIndex = 0;
            this.currencyRatesList[ncid] = row[0];
            this.editMode = false;
            this.reloadCurrencyRatesTable();
          });
    }
  }

  /** Deletes the selected row and triggers a table reload. */
  onDeleteCurrencyRow(event) {
    console.log('*** CurrencyRatesComponent onDeleteCurrencyRow: ' + JSON.stringify(event));

    if (this.selectedCurrencyRatesItem) {
      console.log('*** CurrencyRatesComponent onDeleteCurrencyRow selection: ' + JSON.stringify(this.selectedCurrencyRatesItem));

      let deletedIndex = this.currencyRatesList.findIndex(e => e.CRID == this.selectedCurrencyRatesItem.CRID);

      console.log('*** CurrencyRatesComponent onDeleteCurrencyRow deletedIndex: ' + deletedIndex);

      this.editRatesQuery =
        this.restapi.deleteCurrencyRateRowById(this.selectedCurrencyRatesItem.CRID).subscribe((rows) => {
          console.log('*** CurrencyRatesComponent onDeleteCurrencyRow subscribe: ' + JSON.stringify(rows));
          this.reloadCurrencyRatesTable();

//          this.currencyRatesList.slice(deletedIndex, 1);
        })
    }
  }

  /** Logs the sort event (diagnostic only). */
  onSortCurrRates(event) {
    console.log('*** CurrencyRatesComponent onSortCurrRates: ' + JSON.stringify(event));
  }

  /** Forces a table reload on explicit refresh request. */
  refreshTable(event) {
    console.log('*** CurrencyRatesComponent refreshTable: ' + JSON.stringify(event));
    this.reloadCurrencyRatesTable();
  }

  /** Writes a Blob to an `.xlsx` file via `file-saver`. */
  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      const EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });

      saveAs(data, fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION);
    });
  }

  /** Exports the current currency rates list to an xlsx file. */
  onExportData() {
    console.log('*** HomePaidOrdersListComponent onExportData: ')

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.currencyRatesList);

      const exportData: any =
        this.currencyRatesList.map(e => ({

          'Код валюты': e.CURCODE,
          'Валюта': e.CURNAME,
          'Дата': new Date(e.CRDATE),
          'Курс': e.CRVALUE,

        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { currency: worksheet }, SheetNames: ['currency'] };

      const excelBuffer: any = xlsx.write(workbook, { bookType: "xlsx", type: "array" });

      this.saveAsExcelFile(excelBuffer, "currency");
    });
  }
}
